import React from 'react';
import { Layout, Typography, Card, Row, Col, List, Divider } from 'antd';
import { 
  HomeOutlined, 
  ToolOutlined, 
  FileTextOutlined, 
  UserOutlined,
  GiftOutlined,
  QuestionCircleOutlined,
  EnvironmentOutlined,
  CalendarOutlined,
  MailOutlined,
  LoginOutlined
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { Header, Footer } from '../../components/common/layout';
import { theme } from '../../styles/theme';

const { Content } = Layout;
const { Title, Text, Paragraph } = Typography;

const SitemapPage = () => {
  const mainPages = [
    {
      title: 'Home',
      path: '/',
      description: 'Main landing page with service overview and quote request',
      icon: <HomeOutlined style={{ color: theme.colors.primary }} />
    },
    {
      title: 'Services',
      path: '/services',
      description: 'Complete list of home maintenance and improvement services',
      icon: <ToolOutlined style={{ color: theme.colors.primary }} />
    },
    {
      title: 'Get Quote',
      path: '/quote',
      description: 'Request a personalized quote for your home services',
      icon: <EnvironmentOutlined style={{ color: theme.colors.primary }} />
    },
    {
      title: 'About Us',
      path: '/about',
      description: 'Learn about HSP and our commitment to quality service',
      icon: <UserOutlined style={{ color: theme.colors.primary }} />
    }
  ];

  const programPages = [
    {
      title: 'Referral Program',
      path: '/referral-program',
      description: 'Earn rewards by referring friends and family',
      icon: <GiftOutlined style={{ color: theme.colors.primary }} />
    },
    {
      title: 'Workshops',
      path: '/workshops',
      description: 'Educational workshops and home maintenance tips',
      icon: <CalendarOutlined style={{ color: theme.colors.primary }} />
    },
    {
      title: 'Email Marketing',
      path: '/email-marketing',
      description: 'Stay updated with our latest offers and tips',
      icon: <MailOutlined style={{ color: theme.colors.primary }} />
    }
  ];

  const userPages = [
    {
      title: 'Sign Up',
      path: '/signup',
      description: 'Create your HSP account to manage services',
      icon: <LoginOutlined style={{ color: theme.colors.primary }} />
    },
    {
      title: 'User Dashboard',
      path: '/dashboard',
      description: 'Manage your account, services, and appointments',
      icon: <UserOutlined style={{ color: theme.colors.primary }} />
    }
  ];

  const supportPages = [
    {
      title: 'FAQ',
      path: '/faq',
      description: 'Frequently asked questions about our services',
      icon: <QuestionCircleOutlined style={{ color: theme.colors.primary }} />
    }
  ];

  const renderPageList = (pages, title) => (
    <Card 
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {title}
        </div>
      }
      style={{ 
        marginBottom: '24px',
        borderRadius: '12px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
      }}
    >
      <List
        dataSource={pages}
        renderItem={(page) => (
          <List.Item style={{ padding: '16px 0', borderBottom: '1px solid #f0f0f0' }}>
            <List.Item.Meta
              avatar={page.icon}
              title={
                <Link 
                  to={page.path} 
                  style={{ 
                    color: theme.colors.primary,
                    fontSize: '16px',
                    fontWeight: '600',
                    textDecoration: 'none'
                  }}
                >
                  {page.title}
                </Link>
              }
              description={
                <Text style={{ color: theme.colors.gray, fontSize: '14px' }}>
                  {page.description}
                </Text>
              }
            />
          </List.Item>
        )}
      />
    </Card>
  );

  return (
    <Layout style={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      <Header />
      <Content style={{ paddingTop: '80px', paddingBottom: '40px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
          {/* Hero Section */}
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <Title 
              level={1} 
              style={{ 
                color: theme.colors.primary,
                fontFamily: theme.fonts.primary,
                fontWeight: '700',
                marginBottom: '16px'
              }}
            >
              Site Map
            </Title>
            <Paragraph 
              style={{ 
                fontSize: '18px',
                color: theme.colors.gray,
                maxWidth: '600px',
                margin: '0 auto',
                fontFamily: theme.fonts.primary
              }}
            >
              Navigate through all pages and sections of our website. Find exactly what you're looking for with our organized site structure.
            </Paragraph>
          </div>

          {/* Main Pages */}
          <Row gutter={[24, 24]}>
            <Col xs={24} lg={12}>
              {renderPageList(mainPages, 'Main Pages')}
            </Col>
            <Col xs={24} lg={12}>
              {renderPageList(programPages, 'Programs & Services')}
            </Col>
          </Row>

          <Row gutter={[24, 24]}>
            <Col xs={24} lg={12}>
              {renderPageList(userPages, 'User Account')}
            </Col>
            <Col xs={24} lg={12}>
              {renderPageList(supportPages, 'Support & Help')}
            </Col>
          </Row>

          {/* Additional Information */}
          <Card 
            style={{ 
              marginTop: '32px',
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              backgroundColor: theme.colors.primary,
              color: 'white'
            }}
          >
            <div style={{ textAlign: 'center', padding: '24px' }}>
              <Title 
                level={3} 
                style={{ 
                  color: 'white',
                  fontFamily: theme.fonts.primary,
                  marginBottom: '16px'
                }}
              >
                Need Help Finding Something?
              </Title>
              <Paragraph 
                style={{ 
                  color: 'white',
                  fontSize: '16px',
                  marginBottom: '24px',
                  fontFamily: theme.fonts.primary
                }}
              >
                Can't find what you're looking for? Contact us directly for assistance.
              </Paragraph>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', flexWrap: 'wrap' }}>
                <Text style={{ color: 'white', fontSize: '16px', fontFamily: theme.fonts.primary }}>
                  📞 888.504.6944
                </Text>
                <Text style={{ color: 'white', fontSize: '16px', fontFamily: theme.fonts.primary }}>
                  ✉️ info@hammersawpaint.com
                </Text>
              </div>
            </div>
          </Card>
        </div>
      </Content>
      <Footer />
    </Layout>
  );
};

export default SitemapPage;
